/// <amd-module name="Test/TestResultDetails/Components/StackTrace" />
import * as React from "react";
import { IViewContextData, ViewContext } from "Test/common-util/Common";
import { getProjectName } from "Test/common-util/Common.Utils";
import * as Telemetry from "Test/common-util/Telemetry";
import {
    ILinkedStackTraceInfo,
    IParsedStackTraceInfo,
    ISourceInfoProviderFactoryService,
    IStackTraceLinkCreatorService,
    IStackTraceParserService,
} from "Test/StackTraceParser/Contracts";
import * as TelemetryConstants from "Test/TestResultDetails/TelemetryConstants";
import { equals } from "VSS/Core/Util/String";
import { FPSLinkComponent } from "VSS/Platform/Components/FPSLink";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";

export interface IStackTraceProps extends IVssComponentProperties {
    stackTrace: string;
    linkedStackTrace?: boolean;
    viewContext: IViewContextData;
}

export interface IStackTraceState {
    isParsing: boolean;
    linkedStackTraceInfo: any;
}

export class StackTrace extends VssComponent<IStackTraceProps, IStackTraceState, IVssComponentContext> {

    constructor(props: IStackTraceProps, context: IVssComponentContext) {
        super(props, context);
        this.initializeState();
    }

    public componentDidMount(): void {
        this._tryBuildingStackLinks();
    }

    public componentDidUpdate(prevProps: IStackTraceProps): void {
        let hasStackTraceChanged: boolean = !equals(this.props.stackTrace, prevProps.stackTrace, true);
        if (hasStackTraceChanged) {
            this.initializeState();
            this._tryBuildingStackLinks();
        }
    }

    public render(): JSX.Element {
        if (!this.props.linkedStackTrace || this.state.isParsing || !this.props.stackTrace) {
            return (<div>{this.props.stackTrace}</div>);
        }
        else {
            return (<div>{this._enhanceStackTraceWithLinks(this.state.linkedStackTraceInfo)}</div>);
        }
    }

    private _enhanceStackTraceWithLinks(linkInfo: any[]): JSX.Element[] {

        let stackTraceLink: JSX.Element[] = linkInfo.map((singleLinkInfo, index) => {
            if (!!singleLinkInfo.url) {
                return (
                    <div key={singleLinkInfo.stackTrace + index} onMouseDown={this._onMouseDown} onContextMenu={this._onContextMenu}>
                        <FPSLinkComponent
                            href={singleLinkInfo.url}
                            className={"file-url"}
                            telemetrySource={TelemetryConstants.featureTestTab_StackTraceClicked}>
                            {singleLinkInfo.stackTrace}
                        </FPSLinkComponent>
                    </div>);
            } else {
                return (
                    <div key={singleLinkInfo.stackTrace + index} >
                        {singleLinkInfo.stackTrace}
                    </div>);
            }
        });

        return stackTraceLink;
    }

    // FPSLinkComponent is not capable of logging telemetry if mouse right button or middle button is getting used to click on link.
    // Hence logging telemetry on mouse wheel button click here.
    private _onMouseDown = (event: any) => { 
        // 1 is for middle wheel button.
        // Not capturing right click here because code for right click is not same for left hand and right hand configured mouse.
        if (event.button == 1) {
            Telemetry.publishEvent(this.context.pageContext, this.props.viewContext, TelemetryConstants.featureTestTab_StackTraceClicked, {
                "ClickType": "MouseWheel"
            });
        }
    }

    // FPSLinkComponent is not capable of logging telemetry if mouse right button or middle button is getting used to click on link.
    // Hence logging telemetry on mouse right click here.
    private _onContextMenu = () => {
        Telemetry.publishEvent(this.context.pageContext, this.props.viewContext, TelemetryConstants.featureTestTab_StackTraceClicked, {
            "ClickType": "ContextMenu"
        });
    }

    private async _tryBuildingStackLinks(): Promise<void> {
        if (!this.props.viewContext || this.props.viewContext.viewContext === ViewContext.Release) {
            // Stack trace link building will not happen for release.
            return;
        }

        if (this.props.linkedStackTrace && !!this.props.stackTrace) {
            let sourceBranch = this.props.viewContext.data.mainData.sourceBranch;
            let project = getProjectName(this.context.pageContext);
            let repository = this.props.viewContext.data.mainData.repository.id;
            let repositoryType = this.props.viewContext.data.mainData.repository.type;

            const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
            await contributionService.getContributionAsync("ms.vss-test-web.test-stacktrace-parser");

            const sourceInfoProviderFactory = this.context.pageContext.getService<ISourceInfoProviderFactoryService>("ISourceInfoProviderFactoryService");
            const sourceInfoProvider = sourceInfoProviderFactory.getSourceInfoProvider(this.context, repositoryType, repository, project, sourceBranch);

            if (!sourceInfoProvider) {
                return;
            }

            const stackTraceParser = this.context.pageContext.getService<IStackTraceParserService>("IStackTraceParserService");
            let parsedStackTraceInfo: IParsedStackTraceInfo[] = stackTraceParser.parseStackTrace(this.props.stackTrace);
            const stackTraceLinkCreator = this.context.pageContext.getService<IStackTraceLinkCreatorService>("IStackTraceLinkCreatorService");
            const linkedStackTraceInfo: ILinkedStackTraceInfo[] = await stackTraceLinkCreator.createLinks(sourceInfoProvider, parsedStackTraceInfo);
            this.setState({
                isParsing: false,
                linkedStackTraceInfo: linkedStackTraceInfo
            });

            if (linkedStackTraceInfo.some((singleLinkInfo) => { return (!!singleLinkInfo.url) })) {
                Telemetry.publishEvent(this.context.pageContext, this.props.viewContext, TelemetryConstants.featureTestTab_StackTraceLinksCreated, {});
            }
        }
    }

    private initializeState(): void {
        this.state = {
            isParsing: true,
            linkedStackTraceInfo: []
        };
    }
}

